import type {
  AiOriginalImage,
  ProductCatalogVariationFragment,
} from 'src/shared/graphql/generated';

import type {
  AiDisplayImageFragment,
  AiGeneratedImageFragment,
  AiOriginalImageFragment,
} from './graphql/generated';
import { CreatedFrom } from './static_values';

export type Aspect = 'interior' | 'exterior';

export type AiOriginalOrGeneratedImage =
  | AiOriginalImageFragment
  | AiGeneratedImageFragment;

export type AiOriginalOrGeneratedOrDisplayImage =
  | AiOriginalOrGeneratedImage
  | AiDisplayImageFragment;

/**
 * Image Type Guards: In addition to determining if a given image is a more specific type, with the boolean result,
 * these guards narrow down the type of the image so you can access properties only available on specific types.
 *
 * Example usage:
 *
 * const image: AiOriginalOrGeneratedOrDisplayImage = {...}
 *
 * const incorrectImagePath = image?.generatedImagePath
 * //ESLint error: Property 'generatedImagePath' does not exist on type 'AiOriginalOrGeneratedOrDisplayImage'.
 *
 * const correctImagePath = isAiGenerated(image) && image.generatedImagePath  //Valid!
 *
 * We are using these type guards within the homeownerApp module to drive different behavior between user uploaded/generated
 * images, and also drive different behavior
 * between original and generated images (e.g. favoriting is restricted to only generated images). For more information on type
 * narrowing and its usage in TypeScript please check out the docs here:
 * @see {@link https://www.typescriptlang.org/docs/handbook/2/narrowing.html#using-type-predicates}
 */

export const isGenerated = (
  image: AiOriginalOrGeneratedOrDisplayImage | undefined | null,
): image is AiGeneratedImageFragment =>
  !!image && 'aiOriginalImageId' in image && !('createdFrom' in image);

export const isOriginal = (
  image: AiOriginalOrGeneratedOrDisplayImage | undefined | null,
): image is AiOriginalImageFragment =>
  !!image &&
  image.__typename !== 'AiDisplayImage' &&
  !('aiOriginalImageId' in image);

export const isDisplay = (
  image: AiOriginalOrGeneratedOrDisplayImage | undefined | null,
): image is AiDisplayImageFragment => !!image && 'createdFrom' in image;

export const isAiDisplay = (
  image: AiDisplayImageFragment | undefined | null,
): image is AiDisplayImageFragment => !!image && 'id' in image;

export const isAiOriginal = (
  image: AiOriginalOrGeneratedOrDisplayImage | undefined | null,
): image is AiOriginalImage => !!image && isOriginal(image);

export const isAiGenerated = (
  image: AiOriginalOrGeneratedOrDisplayImage | undefined | null,
): image is AiGeneratedImageFragment => !!image && isGenerated(image);

export const isPaintProductVariation = (
  variation: ProductCatalogVariationFragment,
) => !variation.aiPrompt?.name;

export const isMaterialProductVariation = (
  variation: ProductCatalogVariationFragment,
) => !!variation.aiPrompt?.name;

/**
 * `getImagePath`: This function returns the appropriate path for the image url
 * based on the type of the image; it always returns a string value.
 *
 * @param {AiOriginalOrGeneratedOrDisplayImage | undefined | null} image - The image object to retrieve the
 * path from. It can be of type AiOriginalOrGeneratedOrDisplayImage (or any of its subtypes), undefined, or
 * null. If the image is undefined or null, the function returns an empty string.
 *
 * @returns A string representing the image path. If the image is undefined or null,
 * an empty string is returned.
 *
 */

export const getImagePath = (
  image: AiOriginalOrGeneratedOrDisplayImage | undefined | null,
): string => {
  if (isDisplay(image)) return image.aiGeneratedImage.generatedImagePath;
  if (isOriginal(image)) return image.originalImagePath;
  if (isGenerated(image)) return image.generatedImagePath;

  // FIXME: this can lead to silent failures so we should return undefined and
  // incorporate `undefined` into the return type of the passed image type can
  // be `undefined` or `null`
  return '';
};

export const isImageEqual = (
  imageA: AiOriginalOrGeneratedOrDisplayImage,
  imageB: AiOriginalOrGeneratedOrDisplayImage,
): boolean => {
  if (
    ![isOriginal, isGenerated, isDisplay].some(
      guard => guard(imageA) && guard(imageB),
    )
  ) {
    return false;
  }

  return imageA.id === imageB.id;
};

/**
 * Helper for normalizing an image that might be a display image into its
 * associated generated image, unless an original or generated image is passed.
 * In that case, simply return the provided generated or original image.
 **
 * @param image generated or display image
 *
 * @returns generated image
 */
export const pickImage = <
  T extends
    | AiDisplayImageFragment
    | AiGeneratedImageFragment
    | undefined
    | null,
>(
  image: T,
) => {
  if (isDisplay(image)) return image.aiGeneratedImage;

  return image as T extends undefined
    ? Extract<T, AiOriginalOrGeneratedImage | undefined>
    : T extends null
    ? Extract<T, AiOriginalOrGeneratedImage | null>
    : Extract<T, AiOriginalOrGeneratedImage>;
};

/**
 * Determine whether the provided image is the original image that was uploaded
 * (as opposed to a generated image or customized original image)
 *
 */
export const isMyUpload = (image: AiDisplayImageFragment) =>
  image.createdFrom === CreatedFrom.ORIGINAL && !image.aiGeneratedImage.edits;
